import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import type { MenuItemContent } from '../types.js';
import {
  bilingual,
  description,
  image,
  listColumns,
  pagePhotos,
  price,
  priceBlock,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A pizzeria price table: each section is a table whose columns are the sizes its items come
 * in (Small, Medium, Large…), with real photos down the side. Items with a single price span the
 * size columns.
 */

const S = '.mb-t-size-matrix';

/** The size columns of a block: every size label its items use, in first-seen order, up to four. */
function sizeLabels(items: MenuItemContent[]): { label: string; labelAr: string | null }[] {
  const seen = new Map<string, string | null>();
  for (const item of items) for (const v of item.variants) if (!seen.has(v.label)) seen.set(v.label, v.labelAr);
  return [...seen.entries()].slice(0, 4).map(([label, labelAr]) => ({ label, labelAr }));
}

function table(ctx: RenderContext, b: MenuBlock): string {
  const sizes = sizeLabels(b.items);
  const span = Math.max(1, sizes.length);
  const heads = sizes.length
    ? sizes.map((s) => `<th class="sm-size">${escapeHtml(ctx.showEn ? s.label : s.labelAr || s.label)}</th>`).join('')
    : '<th class="sm-size"></th>';
  const rows = b.items
    .map((item) => {
      const cells = item.variants.length
        ? sizes
            .map((s) => {
              const v = item.variants.find((x) => x.label === s.label);
              return `<td class="sm-cell" dir="ltr">${v ? price(ctx, v.price) : '—'}</td>`;
            })
            .join('')
        : `<td class="sm-cell sm-single" colspan="${span}">${priceBlock(ctx, item, 'sm')}</td>`;
      return [
        '<tr class="sm-row">',
        `<td class="sm-dish">${bilingual(ctx, item.title, item.titleAr, 'sm-name')}${description(ctx, item, 'sm-desc')}</td>`,
        cells,
        '</tr>',
      ].join('');
    })
    .join('');
  return [
    '<table class="sm-table">',
    `<thead><tr class="sm-bar"><th class="sm-th">${sectionTitle(ctx, b.section, 'sm-title')}</th>${heads}</tr></thead>`,
    `<tbody>${rows}</tbody>`,
    '</table>',
  ].join('');
}

export const sizeMatrix: MenuTemplate = {
  id: 'size-matrix',
  name: 'Size matrix',
  description: 'Price tables with a column for each size (Small, Medium, Large), and real photos down the side. Made for pizza.',
  defaultTheme: {
    backgroundColor: '#fff8ee',
    surfaceColor: '#ffffff',
    textColor: '#2b2622',
    primaryColor: '#1f1a17',
    accentColor: '#c8102e',
    fontId: 'street',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'pizzeria', name: 'Pizzeria red', theme: {} },
    { id: 'charcoal', name: 'Charcoal', theme: { backgroundColor: '#1c1c1c', surfaceColor: '#262626', textColor: '#f1f1f1', primaryColor: '#ffffff', accentColor: '#e85d04' } },
    { id: 'verde', name: 'Verde', theme: { accentColor: '#2d6a4f', backgroundColor: '#f6f7f0' } },
    { id: 'navy', name: 'Navy', theme: { accentColor: '#1d3557', backgroundColor: '#f4f6fa', fontId: 'cafe' } },
  ],
  cuisines: ['pizza'],
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(
      groupBySection(board),
      listColumns(board, {
        count: portrait ? 1 : 2,
        capacity: portrait ? 84 : 50,
        heading: 4,
        item: { nameEm: 1.35, descEm: 0.8, perLine: portrait ? 44 : 30, gap: 1.5 },
      }),
      { balance: !portrait },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const photos = pagePhotos(ctx, ctx.orientation === 'portrait' ? 3 : 3)
      .map(
        (item) =>
          `<figure class="sm-photo">${image(ctx, item.imageMediaId, 'sm-photo-img')}<figcaption>${escapeHtml(ctx.showEn ? item.title : item.titleAr || item.title)}</figcaption></figure>`,
      )
      .join('');
    const header = [
      '<div class="sm-header">',
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'sm-heading'),
      bilingual(ctx, board.brand.tagline ?? board.brand.name, board.brand.taglineAr ?? board.brand.nameAr, 'sm-tagline'),
      '</div>',
    ].join('');
    const tables = `<div class="sm-cols">${ctx.page.columns
      .map((c) => `<div class="sm-col">${c.map((b) => table(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
    return `<div class="sm-body"><div class="sm-main">${header}${tables}</div>${photos ? `<div class="sm-photos">${photos}</div>` : ''}</div>`;
  },

  css: `
${S}{padding:2.6em 3.2em}
${S} .sm-body{flex:1;min-height:0;display:grid;grid-template-columns:1fr 26em;gap:3em}
${S} .sm-body:not(:has(.sm-photos)){grid-template-columns:1fr}
${S}.mb-portrait .sm-body{grid-template-columns:1fr;grid-template-rows:1fr auto}
${S} .sm-main{min-width:0;min-height:0;display:flex;flex-direction:column}
${S} .sm-header{display:flex;align-items:baseline;gap:1.2em;flex-wrap:wrap;margin-bottom:1.6em;padding-bottom:.6em;
  border-bottom:.3em solid var(--mb-accent)}
${S} .sm-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:4.2em;text-transform:uppercase;
  color:var(--mb-primary);line-height:.95}
${S} .sm-heading.mb-2nd{font-size:2.6em}
${S} .sm-tagline{font-size:1.1em;opacity:.7;text-transform:uppercase;letter-spacing:.18em}
${S} .sm-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(auto-fit,minmax(0,1fr));gap:2.6em}
${S} .sm-col{min-width:0;display:flex;flex-direction:column;gap:1.8em}
${S} .sm-table{width:100%;border-collapse:collapse;table-layout:auto}
${S} .sm-bar th{background:var(--mb-accent);color:#fff;padding:.45em .7em;text-align:center;white-space:nowrap}
${S} .sm-bar th:first-child{text-align:start;border-radius:.5em 0 0 .5em}
${S} .sm-bar th:last-child{border-radius:0 .5em .5em 0}
${S}[dir=rtl] .sm-bar th:first-child{border-radius:0 .5em .5em 0}
${S}[dir=rtl] .sm-bar th:last-child{border-radius:.5em 0 0 .5em}
${S} .sm-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.9em;text-transform:uppercase;line-height:1}
${S} .sm-title.mb-2nd{font-size:1.3em;margin-top:.15em}
${S} .sm-size{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.15em;text-transform:uppercase;min-width:4.2em}
${S} .sm-row:nth-child(even){background:color-mix(in srgb,var(--mb-surface) 70%,transparent)}
${S} .sm-row td{padding:.6em .7em;border-bottom:.08em solid color-mix(in srgb,var(--mb-text) 12%,transparent);vertical-align:middle}
${S} .sm-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.35em;text-transform:uppercase;
  color:var(--mb-primary);line-height:1.1}
${S} .sm-name.mb-2nd{font-size:1.1em;text-transform:none}
${S} .sm-desc{font-size:.8em;opacity:.7;margin-top:.15em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .sm-cell{text-align:center;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.35em;
  color:var(--mb-accent);white-space:nowrap}
${S} .sm-single{text-align:end}
${S} .sm-pricebox{display:inline-flex;flex-direction:column;align-items:flex-end;line-height:1.05}
${S} .sm-old{font-size:.65em;opacity:.55}
${S}.mb-v-charcoal .sm-cell,${S}.mb-v-charcoal .sm-price{color:var(--mb-accent)}
${S} .sm-photos{display:flex;flex-direction:column;gap:1.4em;min-height:0}
${S}.mb-portrait .sm-photos{flex-direction:row}
${S} .sm-photo{position:relative;flex:1;min-height:0;margin:0;border-radius:1em;overflow:hidden;box-shadow:0 .8em 1.8em rgba(0,0,0,.18)}
${S} .sm-photo-img{width:100%;height:100%;object-fit:cover;display:block}
${S}.mb-portrait .sm-photo{height:16em}
${S} .sm-photo figcaption{position:absolute;left:0;right:0;bottom:0;padding:1.4em .9em .6em;color:#fff;font-weight:700;
  background:linear-gradient(transparent,rgba(0,0,0,.65));font-size:1em}
`,
};
